import { useCallback, useEffect, useState } from "react";

import { Button } from "@chakra-ui/button";
import { HStack, Stack, VStack } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import { useFormikContext } from "formik";

import Card from "../../../../components/Common/Card";
import { SelectMenu } from "../../../../components/Common/InputComponents/InputComponents";
import Typography from "../../../../components/Common/Typography";
import V2Service from "../../../../services/v2Service";

import "../../sendmoney.css";

const SelectContactTab = (props) => {
  const { isOpen, onClose, walletList = [] } = props;

  const { getFieldProps, setFieldValue } = useFormikContext();

  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [userList, setUserList] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [supportedCurrenciesLoading, setSupportedCurrenciesLoading] = useState(false);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);

  const contactTypeFieldValue = "organization";
  const payoutCurrencyFieldValue = getFieldProps("payoutCurrency")?.value || {};

  const getUserList = useCallback(async () => {
    try {
      setContactsLoading(true);
      const response = await V2Service.getUserList({ type: contactTypeFieldValue });
      setUserList(JSON.parse(response?.data?.data || []));
    } catch (error) {
      console.log("error", error);
    } finally {
      handleReset("contactId");
      handleReset("paymentCode");
      setContactsLoading(false);
    }
  }, []);

  const getSupportedCurrencies = async () => {
    try {
      setSupportedCurrenciesLoading(true);
      const response = await V2Service.getSupportedCurrencies({ direction: "withdraw" });
      setSupportedCurrencies(response?.data?.data?.docs || []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setSupportedCurrenciesLoading(false);
    }
  };

  const getPaymentMethods = useCallback(async () => {
    try {
      if (!contactTypeFieldValue || !payoutCurrencyFieldValue?.value) return;
      setPaymentMethodsLoading(true);
      const formattedBeneficiaryType = "business";
      const response = await V2Service.getPaymentMethods({
        direction: "withdraw",
        beneficiaryType: formattedBeneficiaryType,
        currency: payoutCurrencyFieldValue?.value,
      });
      setPaymentMethodsList(JSON.parse(response?.data?.data) || []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setPaymentMethodsLoading(false);
    }
  }, [contactTypeFieldValue, payoutCurrencyFieldValue?.value]);

  const handleReset = (fieldName) => {
    switch (fieldName) {
      case "contactType":
        setFieldValue("contactType", "");
        break;
      case "contactId":
        setFieldValue("contactId", "");
        break;
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    getSupportedCurrencies();
  }, []);

  useEffect(() => {
    getPaymentMethods();
  }, [payoutCurrencyFieldValue?.value]);

  return (
    <VStack w={"100%"} marginTop={"15px"} gap="24px">
      <Card>
        <Typography type="body" weight="medium" color="primary">
          Contact Details
        </Typography>

        <HStack w={"100%"} display="flex" justifyContent="space-between" gap="10px" zIndex={"1000"}>
          <SelectMenu
            label="Select Contact"
            isMandatory={true}
            options={userList}
            name="contactId"
            placeholder="Select"
            searchBoxPlaceholder="Search Contact"
            optionBoxPlaceHolder="No Contact Found"
            buttonPlaceholder="Add Contact"
            isDisabled={false}
            isLoading={contactsLoading}
          />
          <SelectMenu
            label="Settlement Currency"
            isMandatory={true}
            options={supportedCurrencies}
            name="payoutCurrency"
            placeholder="Select"
            searchBoxPlaceholder="Search Currency"
            optionBoxPlaceHolder="No Currency Found"
            isDisabled={false}
            isLoading={supportedCurrenciesLoading}
          />
        </HStack>

        <HStack w={"100%"} display="flex" justifyContent="space-between" gap="10px" zIndex={"10"}>
          <SelectMenu
            label="Payment Method"
            isMandatory={true}
            options={paymentMethodsList}
            name="paymentCode"
            placeholder="Select"
            searchBoxPlaceholder="Search Payment Method"
            optionBoxPlaceHolder="No Payment Method Found"
            isDisabled={false}
            isLoading={paymentMethodsLoading}
          />
          <SelectMenu
            label="You Pay From"
            isMandatory={true}
            options={walletList}
            name="walletName"
            placeholder="Select"
            searchBoxPlaceholder="Search Wallet"
            optionBoxPlaceHolder="No Wallet Found"
            isDisabled={false}
          />
        </HStack>

        <Stack direction="row" justifyContent={"end"} w="100%" marginTop={0}>
          <Button variant={"primary"} type="submit" size={"sm"} p={"9px 16px 9px 16px"}>
            Next
          </Button>
        </Stack>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="center" textAlign="center" color="#1A4FD6">
              <Text>Notice</Text>
            </HStack>
          </ModalHeader>
          <ModalBody textAlign="center">
            <Text>This transaction can only be processed during banking hours</Text>
            <Text fontWeight={"bold"} mb={5}>
              9:00 AM - 4:00 PM
            </Text>
            <Text>Please try again during banking hours</Text>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <HStack>
              <Button border="1px" borderColor="#525C76" variant={"primary"} onClick={onClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default SelectContactTab;
